// NCA ECC Compliance Plan Detail Component
// عرض تفاصيل خطة مطابقة NCA ECC-2:2024

const PHASES = [
  {
    id: 1,
    name: 'المرحلة 1: صفر تكلفة',
    deadline: 'اليوم',
    icon: '🟢',
    color: '#22C55E',
    controls: [
      { id: '2-2-7', name: 'سياسة كلمات المرور', status: 'done', priority: 'عالية' },
      { id: '2-2-11', name: 'حماية الشبكات (UFW)', status: 'done', priority: 'حرجة' },
      { id: '2-2-23', name: 'سجلات الأمان', status: 'progress', priority: 'حرجة' },
      { id: '2-2-30', name: 'حماية الاتصالات (SSL)', status: 'done', priority: 'عالية' },
      { id: '2-2-31', name: 'حماية الويب (Helmet)', status: 'done', priority: 'عالية' },
      { id: '2-2-43', name: 'البيانات المنقولة (HTTPS)', status: 'done', priority: 'عالية' },
      { id: '2-2-49', name: 'إدارة المنافذ (Firewall)', status: 'done', priority: 'عالية' },
      { id: '2-3-4', name: 'النسخ الاحتياطي', status: 'progress', priority: 'حرجة' },
    ]
  },
  {
    id: 2,
    name: 'المرحلة 2: <$50/شهر',
    deadline: 'هذا الأسبوع',
    icon: '🟡',
    color: '#EAB308',
    controls: [
      { id: '2-2-1', name: 'إدارة الأصول', status: 'progress', priority: 'عالية' },
      { id: '2-1-20', name: 'إدارة التكوين الأمني', status: 'progress', priority: 'عالية' },
      { id: '2-2-33', name: 'حماية DDoS (Cloudflare)', status: 'pending', priority: 'عالية' },
      { id: '2-2-16', name: 'إدارة الثغرات (Trivy)', status: 'pending', priority: 'حرجة' },
      { id: '2-1-1', name: 'سياسة الأمن السيبراني', status: 'pending', priority: 'عالية' },
      { id: '2-2-5', name: 'إدارة الهوية', status: 'progress', priority: 'حرجة' },
      { id: '2-2-10', name: 'إدارة الجلسات', status: 'progress', priority: 'متوسطة' },
      { id: '2-2-27', name: 'أمن APIs', status: 'progress', priority: 'عالية' },
    ]
  },
  {
    id: 3,
    name: 'المرحلة 3: الشهر القادم',
    deadline: '$100-500/شهر',
    icon: '🔴',
    color: '#EF4444',
    controls: [
      { id: '2-2-6', name: 'المصادقة متعددة العوامل (MFA)', status: 'pending', priority: 'حرجة' },
      { id: '2-2-44', name: 'تشفير البيانات', status: 'pending', priority: 'حرجة' },
      { id: '2-2-18', name: 'اختبار الاختراق (OWASP ZAP)', status: 'pending', priority: 'حرجة' },
      { id: '2-3-2', name: 'خطة استعادة الكوارث', status: 'pending', priority: 'حرجة' },
    ]
  }
];

// Summary stats
const SUMMARY = {
  totalControls: 181,
  applicableControls: 88,
  evidenced: 8,
  inProgress: 30,
  notStarted: 50,
  notApplicable: 93,
  targetCompliance: '30% (شهرين)',
  monthlyBudget: '$5 (Backblaze B2)'
};

function StatusBadge({ status }) {
  const styles = {
    done: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: '✅ مكتمل' },
    progress: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: '🔄 قيد التنفيذ' },
    pending: { bg: 'bg-white/[0.04]', text: 'text-[#94A3B8]', label: '❌ لم يبدأ' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const colors = {
    'حرجة': 'text-red-400 bg-red-500/10',
    'عالية': 'text-amber-400 bg-amber-500/10',
    'متوسطة': 'text-blue-400 bg-blue-500/10',
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded ${colors[priority] || 'text-gray-400 bg-gray-500/10'}`}>
      {priority}
    </span>
  );
}

export default function NCAEccPlan({ realProject }) {
  // Calculate progress from real data
  const progressDetails = realProject?.progressDetails || [];
  const overallProgress = realProject?.progress || 0;
  const completedItems = realProject?.completedItems || 0;
  const totalItems = realProject?.totalItems || 0;

  return (
    <div className="space-y-6">
      {/* Header Summary */}
      <div className="p-5 rounded-2xl bg-gradient-to-r from-red-500/10 to-rose-500/5 border border-red-500/20">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-500/25 flex items-center justify-center text-2xl">
            🔐
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">خطة مطابقة NCA ECC-2:2024</h3>
            <p className="text-[12px] text-[#94A3B8]">ضوابط الأمن السيبراني الأساسية — سناجم تكنولوجيا</p>
          </div>
          <div className="mr-auto text-center">
            <div className="text-3xl font-bold text-red-400">{overallProgress}%</div>
            <div className="text-[10px] text-[#64748B]">التقدم الكلي</div>
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white/[0.05] rounded-xl p-3 text-center">
            <div className="text-xl font-bold text-white">{SUMMARY.totalControls}</div>
            <div className="text-[10px] text-[#94A3B8]">إجمالي الضوابط</div>
          </div>
          <div className="bg-white/[0.05] rounded-xl p-3 text-center">
            <div className="text-xl font-bold text-emerald-400">{SUMMARY.evidenced}</div>
            <div className="text-[10px] text-[#94A3B8]">ضوابط محققة</div>
          </div>
          <div className="bg-white/[0.05] rounded-xl p-3 text-center">
            <div className="text-xl font-bold text-amber-400">{SUMMARY.inProgress}</div>
            <div className="text-[10px] text-[#94A3B8]">قيد التنفيذ</div>
          </div>
          <div className="bg-white/[0.05] rounded-xl p-3 text-center">
            <div className="text-xl font-bold text-[#64748B]">{SUMMARY.notStarted}</div>
            <div className="text-[10px] text-[#94A3B8]">لم تبدأ</div>
          </div>
        </div>
      </div>

      {/* Progress Checklist from ops-api */}
      {progressDetails.length > 0 && (
        <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <h4 className="text-[13px] font-bold text-white mb-3">📋 حالة التنفيذ الفعلي</h4>
          <div className="space-y-2">
            {progressDetails.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02]">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm
                  ${item.done ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
                  {item.done ? '✅' : '❌'}
                </div>
                <div className="flex-1">
                  <span className="text-[13px] font-medium text-white">{item.label}</span>
                  {item.detail && (
                    <span className="text-[10px] text-[#64748B] mr-2">({item.detail})</span>
                  )}
                </div>
                <div className="text-[10px] text-[#475569] bg-white/[0.04] rounded-lg px-2 py-1">
                  وزن {item.weight}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phases */}
      {PHASES.map((phase, phaseIdx) => {
        const phaseDone = phase.controls.filter(c => c.status === 'done').length;
        const phaseTotal = phase.controls.length;
        const phaseProgress = Math.round((phaseDone / phaseTotal) * 100);
        
        return (
          <div key={phase.id} 
            className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden"
            style={{ animation: `fadeInUp 0.5s ease-out ${phaseIdx * 100}ms both` }}>
            
            {/* Phase Header */}
            <div className="p-4 border-b border-white/[0.06] flex items-center gap-3"
              style={{ background: `${phase.color}08` }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                style={{ background: `${phase.color}15`, border: `1px solid ${phase.color}25` }}>
                {phase.icon}
              </div>
              <div className="flex-1">
                <h4 className="text-[14px] font-bold text-white">{phase.name}</h4>
                <div className="text-[10px] text-[#64748B]">
                  {phaseDone}/{phaseTotal} ضوابط • {phase.deadline}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold" style={{ color: phase.color }}>{phaseProgress}%</div>
                <div className="text-[9px] text-[#475569]">إنجاز</div>
              </div>
              {/* Mini progress bar */}
              <div className="w-20">
                <div className="w-full h-2 bg-white/[0.06] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${phaseProgress}%`, background: phase.color }} />
                </div>
              </div>
            </div>

            {/* Controls List */}
            <div className="p-4 space-y-2">
              {phase.controls.map((control, i) => (
                <div key={control.id} 
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all"
                  style={{ animation: `fadeInUp 0.3s ease-out ${i * 40}ms both` }}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-mono font-bold
                    ${control.status === 'done' ? 'bg-emerald-500/15 text-emerald-400' : 
                      control.status === 'progress' ? 'bg-amber-500/15 text-amber-400' : 
                      'bg-white/[0.04] text-[#64748B]'}`}>
                    {control.status === 'done' ? '✓' : control.id.split('-').pop()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-medium text-white">{control.name}</span>
                      <span className="text-[9px] text-[#475569] font-mono">{control.id}</span>
                    </div>
                  </div>
                  <PriorityBadge priority={control.priority} />
                  <StatusBadge status={control.status} />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Budget Summary */}
      <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <h4 className="text-[13px] font-bold text-white mb-3">💰 ملخص الميزانية</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-emerald-500/10 rounded-xl p-3 text-center">
            <div className="text-lg font-bold text-emerald-400">$0</div>
            <div className="text-[10px] text-[#94A3B8]">المرحلة 1 (مجاني)</div>
          </div>
          <div className="bg-amber-500/10 rounded-xl p-3 text-center">
            <div className="text-lg font-bold text-amber-400">&lt;$50</div>
            <div className="text-[10px] text-[#94A3B8]">المرحلة 2 (شهرياً)</div>
          </div>
          <div className="bg-red-500/10 rounded-xl p-3 text-center">
            <div className="text-lg font-bold text-red-400">$100-500</div>
            <div className="text-[10px] text-[#94A3B8]">المرحلة 3 (شهرياً)</div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/[0.04] text-center">
          <span className="text-[11px] text-[#94A3B8]">الهدف: </span>
          <span className="text-[11px] text-white font-bold">30% مطابقة خلال شهرين</span>
          <span className="text-[11px] text-[#64748B]"> • التكلفة الشهرية المتوقعة: $5</span>
        </div>
      </div>
    </div>
  );
}
